import CommonApi from "../common/CommonApi";
import axios from "axios";
import SuccessResponse from "../dto/common/SuccessResponse";
import CommonRequestBodyDto from "../dto/common/CommonRequestBodyDto";
import DeviceDto from "../dto/device/DeviceDto";
import DeviceStatusDto from "../dto/device/DeviceStatusDto";
import DeviceDetailDto from "../dto/device/DeviceDetailDto";
import DeviceHistoryResDto from "../dto/device/DeviceHistoryResDto";
import DeviceErrorDto from "../dto/device/DeviceErrorDto";
import DeviceSettingsDto from "../dto/device/DeviceSettingsDto";
import DeviceSettingsSaveReqDto from "../dto/device/DeviceSettingsSaveReqDto";
import SubDeviceDto from "../dto/device/SubDeviceDto";

class RealDeviceApi extends CommonApi {
    async getDeviceList({ shopCode }: { shopCode: string }): Promise<SuccessResponse<DeviceDto[]>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/devices/device`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<DeviceDto[]>>(
            `${this.BASE_SERVICE_URL}/devices/device`,
            {
                headers: baseHeaders,
                params: { shopCode },
            }
        );

        return response.data;
    }

    async getDeviceStatus({ shopCode }: { shopCode: string }): Promise<SuccessResponse<DeviceStatusDto>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/devices/device-status`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<DeviceStatusDto>>(
            `${this.BASE_SERVICE_URL}/devices/device-status`,
            {
                headers: baseHeaders,
                params: { shopCode },
            }
        );

        return response.data;
    }

    async getDeviceDetails({ idDevice }: { idDevice: number }): Promise<SuccessResponse<DeviceDetailDto>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/devices/device-details`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<DeviceDetailDto>>(
            `${this.BASE_SERVICE_URL}/devices/device-details`,
            {
                headers: baseHeaders,
                params: { idDevice },
            }
        );

        return response.data;
    }

    async getDeviceDetailsHistory({
        deviceId,
        page = 1,
        pageSize = 20,
    }: {
        deviceId: string;
        page?: number;
        pageSize?: number;
    }): Promise<SuccessResponse<DeviceHistoryResDto>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/devices/${deviceId}/history`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<DeviceHistoryResDto>>(
            `${this.BASE_SERVICE_URL}/devices/${deviceId}/history`,
            {
                headers: baseHeaders,
                params: { page, pageSize },
            }
        );

        return response.data;
    }

    async getDeviceErrorList({
        storeId,
        startDay,
        endDay,
        deviceName,
        errorCode,
    }: {
        storeId: string;
        startDay: string;
        endDay: string;
        deviceName?: string;
        errorCode?: string;
    }): Promise<SuccessResponse<DeviceErrorDto[]>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/stores/${storeId}/error`, {
            headers: baseHeaders,
        });

        // startDay/endDay 는 필수. deviceName/errorCode 는 있을 때만 전달.
        const params: Record<string, string> = { startDay, endDay };
        if (deviceName) params.deviceName = deviceName;
        if (errorCode) params.errorCode = errorCode;

        const response = await axios.get<SuccessResponse<DeviceErrorDto[]>>(
            `${this.BASE_SERVICE_URL}/stores/${storeId}/error`,
            {
                headers: baseHeaders,
                params,
            }
        );

        return response.data;
    }

    async saveDeviceSettings({
        deviceId,
        reqDto,
    }: {
        deviceId: number;
        reqDto: CommonRequestBodyDto<DeviceSettingsSaveReqDto>;
    }): Promise<SuccessResponse<{}>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/devices/${deviceId}/settings`, {
            headers: baseHeaders,
        });

        const response = await axios.post<SuccessResponse<{}>>(
            `${this.BASE_SERVICE_URL}/devices/${deviceId}/settings`,
            reqDto,
            {
                headers: baseHeaders,
            }
        );

        return response.data;
    }

    async getDeviceSettings({ deviceId }: { deviceId: number }): Promise<SuccessResponse<DeviceSettingsDto[]>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/devices/${deviceId}/settings`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<DeviceSettingsDto[]>>(
            `${this.BASE_SERVICE_URL}/devices/${deviceId}/settings`,
            {
                headers: baseHeaders,
            }
        );

        return response.data;
    }

    async getSubDevice({ subDeviceId }: { subDeviceId: string }): Promise<SuccessResponse<SubDeviceDto>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/devices/subdevice/${subDeviceId}`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<SubDeviceDto>>(
            `${this.BASE_SERVICE_URL}/devices/subdevice/${subDeviceId}`,
            {
                headers: baseHeaders,
            }
        );

        return response.data;
    }

    async getSubDeviceByStore({
        storeId,
        deviceId,
    }: {
        storeId: string;
        deviceId: string;
    }): Promise<SuccessResponse<SubDeviceDto[]>> {
        const baseHeaders = this.makeBaseHeader();

        await axios.options(`${this.BASE_SERVICE_URL}/devices/subdevice`, {
            headers: baseHeaders,
        });

        const response = await axios.get<SuccessResponse<SubDeviceDto[]>>(
            `${this.BASE_SERVICE_URL}/devices/subdevice`,
            {
                headers: baseHeaders,
                params: { storeid: storeId, deviceid: deviceId },
            }
        );

        return response.data;
    }
}

export default RealDeviceApi;
